import React, { useContext } from 'react';
import styled from 'styled-components';
import {
  allMetricKinds,
  isProfitMetricAvailable,
  isReturnsMetricAvailable,
  MetricKind,
} from '@/webapi/models';
import { formatEnum } from '@/utils/types';
import {
  GoalChip,
  GoalsWrapper,
} from '@/features/editor/widgets/shared/inline-variants-layout/goals';
import { Experience, useExperienceApi } from '@/webapi/use-experience-api';
import { breakpoints } from '@/components/responsive';
import { SummaryContext } from '@/features/details/summary/context';
import { scrollToSelector } from '@/utils/browser';
import { clone } from '@/features/editor/widgets/shared/modals/audience/facebook-audience/ad-search/shared';
import { getCustomExpEventName } from '@/features/details/summary/see-more-reports-list';

type Props = { experience: Experience; setExperience: (e: Experience) => void };

export function MoreMetrics({ experience, setExperience }: Props) {
  const { loading, setExperienceGoals } = useExperienceApi(
    experience.lastDevicePreview,
    experience,
  );
  const { hasMetric, getCustomMetricName, primaryGoal } =
    useContext(SummaryContext);
  const metricKinds = allMetricKinds(experience)
    .filter(allowProfit(experience))
    .filter(allowedCustomMetrics(hasMetric))
    .filter(allowReturns(experience))
    .filter(allowCustomExpEvents(experience));

  return (
    <Container>
      <Title>Would like to see more reports?</Title>
      <MetricsList>
        {metricKinds.map((metric) => {
          const onClick = async () => {
            if (loading || metric === primaryGoal) {
              return;
            }
            const cloned = clone(experience) as Experience;
            cloned.goals.push(metric);
            await setExperienceGoals(experience.primaryGoal, [
              ...cloned.goals,
              experience.primaryGoal,
            ]);
            setExperience(cloned);
            scrollToSelector(`div[title="${metric}"]`);
          };
          return !selectedGoal(experience, metric) ? (
            <Chip
              focused={selectedGoal(experience, metric)}
              key={metric}
              onClick={onClick}
            >
              {getCustomExpEventName(metric, experience.elementTracking) ||
                getCustomMetricName(metric) ||
                formatEnum(metric, true)}
            </Chip>
          ) : null;
        })}
      </MetricsList>
    </Container>
  );
}

const Title = styled.p`
  user-select: none;
  margin: 0 auto 4rem auto;
  color: #b8c2c9;
  letter-spacing: 0.48px;
  font-family: Inter, serif;
  font-weight: 800;
  font-size: 2.5rem;
  text-align: center;

  ${breakpoints.down(`md`)} {
    font-size: 1.8rem;
  }
`;

const Chip = styled(GoalChip)`
  && {
    background-color: #cfd2d9;
    color: #0c0c0c;
    margin: 1rem;
    font-weight: 700;
    font-size: 1.4rem;
    border: none;
    text-transform: uppercase;
  }
`;

export const MetricsList = styled(GoalsWrapper)`
  && {
    margin: 0 auto;
    max-width: 100rem;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }
`;

export function selectedGoal(experience: Experience, metric: MetricKind) {
  return experience.goals?.includes?.(metric);
}

export function allowProfit(experience: Experience) {
  return (m) => {
    if (isProfitMetricAvailable(experience?.metricsStartAt)) {
      return true;
    }
    return !(m === MetricKind.PROFIT || m === MetricKind.PROFIT_PER_SESSION);
  };
}

export function allowedCustomMetrics(
  hasMetric: (metric: MetricKind) => boolean,
) {
  return (m) => {
    if (isCustomMetric(m)) {
      return hasMetric(m);
    }
    return true;
  };
}

const isCustomMetric = (m) => m.startsWith(`CUSTOM`);

export function allowReturns(experience: Experience) {
  return (m) => {
    if (isReturnsMetricAvailable(experience?.metricsStartAt)) {
      return true;
    }
    return !(m === MetricKind.RETURNS || m === MetricKind.RETURNS_PER_SESSION);
  };
}

export function allowCustomExpEvents(experience: Experience) {
  const set = new Set(experience?.elementTracking?.map((e) => e.ordinal) || []);
  return (m: MetricKind) => {
    const toString = m?.toString();
    return (
      !toString?.startsWith(`EVENT_`) ||
      set.has(
        parseInt(toString?.replace(`EVENT_`, ``)?.replace(`_RATE`, ``), 10),
      )
    );
  };
}

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  width: 70%;
  margin: 0 auto;
`;
